/** First-party acquisition attribution. No email, IP address, or fingerprint is stored. */
export const ATTRIBUTION_COOKIE = 'ppfe_attribution';
export const ATTRIBUTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

const MAX_VALUE_LENGTH = 160;
const CLICK_ID_LENGTH = 255;

export interface AttributionTouch {
	landing_path: string;
	referrer_host: string;
	utm_source: string;
	utm_medium: string;
	utm_campaign: string;
	utm_term: string;
	utm_content: string;
	gclid: string;
	fbclid: string;
	msclkid: string;
	captured_at: string;
}

export interface Attribution {
	v: 1;
	first: AttributionTouch;
	latest: AttributionTouch;
}

function clean(value: string | null | undefined, max = MAX_VALUE_LENGTH): string {
	return (value ?? '')
		.trim()
		.replace(/\p{Cc}/gu, '')
		.slice(0, max);
}

function referrerHost(request: Request, siteUrl: URL): string {
	const raw = request.headers.get('referer');
	if (!raw) return '';
	try {
		const referrer = new URL(raw);
		return referrer.origin === siteUrl.origin ? '' : clean(referrer.hostname.toLowerCase());
	} catch {
		return '';
	}
}

function browserReferrerHost(siteUrl: URL): string {
	if (!document.referrer) return '';
	try {
		const referrer = new URL(document.referrer);
		return referrer.origin === siteUrl.origin ? '' : clean(referrer.hostname.toLowerCase());
	} catch {
		return '';
	}
}

export function touchFromRequest(request: Request, url: URL): AttributionTouch {
	const params = url.searchParams;
	return {
		landing_path: clean(url.pathname, 255) || '/',
		referrer_host: referrerHost(request, url),
		utm_source: clean(params.get('utm_source')),
		utm_medium: clean(params.get('utm_medium')),
		utm_campaign: clean(params.get('utm_campaign')),
		utm_term: clean(params.get('utm_term')),
		utm_content: clean(params.get('utm_content')),
		gclid: clean(params.get('gclid'), CLICK_ID_LENGTH),
		fbclid: clean(params.get('fbclid'), CLICK_ID_LENGTH),
		msclkid: clean(params.get('msclkid'), CLICK_ID_LENGTH),
		captured_at: new Date().toISOString()
	};
}

export function isAttributedTouch(touch: AttributionTouch): boolean {
	return Boolean(
		touch.referrer_host ||
		touch.utm_source ||
		touch.utm_medium ||
		touch.utm_campaign ||
		touch.utm_term ||
		touch.utm_content ||
		touch.gclid ||
		touch.fbclid ||
		touch.msclkid
	);
}

function validTouch(value: unknown): value is AttributionTouch {
	if (!value || typeof value !== 'object') return false;
	const touch = value as Partial<AttributionTouch>;
	return (
		typeof touch.landing_path === 'string' &&
		typeof touch.referrer_host === 'string' &&
		typeof touch.utm_source === 'string' &&
		typeof touch.utm_medium === 'string' &&
		typeof touch.utm_campaign === 'string' &&
		typeof touch.utm_term === 'string' &&
		typeof touch.utm_content === 'string' &&
		typeof touch.gclid === 'string' &&
		typeof touch.fbclid === 'string' &&
		typeof touch.msclkid === 'string' &&
		typeof touch.captured_at === 'string'
	);
}

export function parseAttribution(raw: string | undefined | null): Attribution | null {
	if (!raw || raw.length > 4096) return null;
	try {
		const value: unknown = JSON.parse(raw);
		if (!value || typeof value !== 'object') return null;
		const attribution = value as Partial<Attribution>;
		if (attribution.v !== 1 || !validTouch(attribution.first) || !validTouch(attribution.latest)) {
			return null;
		}
		return attribution as Attribution;
	} catch {
		return null;
	}
}

export function updateAttribution(
	existing: Attribution | null,
	touch: AttributionTouch
): Attribution {
	if (!existing) return { v: 1, first: touch, latest: touch };
	return isAttributedTouch(touch) ? { ...existing, latest: touch } : existing;
}

/** Creem metadata is flat so it remains visible in checkout retrieval and webhooks. */
export function attributionToMetadata(attribution: Attribution | null): Record<string, string> {
	if (!attribution) return {};
	const first = attribution.first;
	const latest = attribution.latest;
	return {
		first_source: first.utm_source || first.referrer_host || 'direct',
		first_medium: first.utm_medium || (first.referrer_host ? 'referral' : 'none'),
		first_campaign: first.utm_campaign,
		first_landing: first.landing_path,
		latest_source: latest.utm_source || latest.referrer_host || 'direct',
		latest_medium: latest.utm_medium || (latest.referrer_host ? 'referral' : 'none'),
		latest_campaign: latest.utm_campaign,
		latest_term: latest.utm_term,
		latest_content: latest.utm_content,
		gclid: latest.gclid,
		fbclid: latest.fbclid,
		msclkid: latest.msclkid
	};
}

export function metadataToAttribution(
	metadata: Record<string, unknown> | undefined
): Record<string, string> {
	if (!metadata) return {};
	const allowed = [
		'first_source',
		'first_medium',
		'first_campaign',
		'first_landing',
		'latest_source',
		'latest_medium',
		'latest_campaign',
		'latest_term',
		'latest_content',
		'gclid',
		'fbclid',
		'msclkid',
		'placement'
	] as const;
	return Object.fromEntries(
		allowed.map((key) => [key, clean(typeof metadata[key] === 'string' ? metadata[key] : '')])
	);
}

/** Captures only after analytics consent; called from the root layout in the browser. */
export function captureBrowserAttribution(): void {
	const url = new URL(window.location.href);
	const params = url.searchParams;
	const match = document.cookie.match(/(?:^|;\s*)ppfe_attribution=([^;]*)/);
	let existing: Attribution | null = null;
	if (match) {
		try {
			existing = parseAttribution(decodeURIComponent(match[1]));
		} catch {
			existing = null;
		}
	}

	const touch: AttributionTouch = {
		landing_path: clean(url.pathname, 255) || '/',
		referrer_host: browserReferrerHost(url),
		utm_source: clean(params.get('utm_source')),
		utm_medium: clean(params.get('utm_medium')),
		utm_campaign: clean(params.get('utm_campaign')),
		utm_term: clean(params.get('utm_term')),
		utm_content: clean(params.get('utm_content')),
		gclid: clean(params.get('gclid'), CLICK_ID_LENGTH),
		fbclid: clean(params.get('fbclid'), CLICK_ID_LENGTH),
		msclkid: clean(params.get('msclkid'), CLICK_ID_LENGTH),
		captured_at: new Date().toISOString()
	};
	const value = encodeURIComponent(JSON.stringify(updateAttribution(existing, touch)));
	const secure = window.location.protocol === 'https:' ? '; secure' : '';
	document.cookie = `${ATTRIBUTION_COOKIE}=${value}; path=/; max-age=${ATTRIBUTION_COOKIE_MAX_AGE}; samesite=lax${secure}`;
}
